import type { DosagePlan } from "@bloom/shared";
const input = "w-full rounded-lg border border-gray-300 p-2 text-sm";
export function DosagePlansEditor({
  plans,
  products,
  onChange,
}: {
  plans: DosagePlan[];
  products: { _id: string; displayName: string }[];
  onChange: (plans: DosagePlan[]) => void;
}) {
  const patch = (id: string, changes: Partial<DosagePlan>) =>
    onChange(plans.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  return (
    <section className="space-y-4">
      <h2 className="font-semibold">Dosage plans</h2>
      <p className="text-sm text-gray-500">
        Each plan maps an explicit dose to a SKU. Multiple plans may use the
        same SKU. Dose labels describe the prescribed amount, not vial
        concentration. Every assigned SKU needs a plan once plans are enabled.
      </p>
      {plans.map((p) => (
        <div
          key={p.id}
          className="rounded-lg border p-4 grid gap-3 sm:grid-cols-2"
        >
          <label className="text-sm">
            Plan name
            <input
              className={input}
              value={p.name}
              onChange={(e) => patch(p.id, { name: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Dose and frequency
            <input
              className={input}
              value={p.doseLabel}
              placeholder="Enter approved dose and frequency"
              onChange={(e) => patch(p.id, { doseLabel: e.target.value })}
            />
          </label>
          <label className="text-sm sm:col-span-2">
            Prescription SIG
            <input className={input} maxLength={150} value={p.prescriptionSig || ""}
              placeholder="Complete prescription instructions, including route and frequency"
              onChange={(e) => patch(p.id, { prescriptionSig: e.target.value || undefined })} />
            <span className="text-xs text-gray-500">Used for this plan unless the clinician supplies different instructions.</span>
          </label>
          <label className="text-sm">
            Treatment path
            <select
              className={input}
              value={p.path}
              onChange={(e) =>
                patch(p.id, { path: e.target.value as DosagePlan["path"] })
              }
            >
              <option value="starter">Starter</option>
              <option value="continuation">Continuation</option>
            </select>
          </label>
          <label className="text-sm">
            Mapped SKU
            <select
              className={input}
              value={p.productId}
              onChange={(e) => patch(p.id, { productId: e.target.value })}
            >
              <option value="">Choose SKU</option>
              {products.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            Allowed states (optional, comma-separated codes)
            <input
              className={input}
              value={p.allowedStates?.join(", ") || ""}
              onChange={(e) =>
                patch(p.id, {
                  allowedStates: e.target.value
                    ? e.target.value
                        .toUpperCase()
                        .split(",")
                        .map((s) => s.trim())
                    : undefined,
                })
              }
            />
            <span className="text-xs text-gray-500">
              Blank adds no plan-specific restriction. Clinic coverage still
              applies.
            </span>
          </label>
          <button
            type="button"
            className="text-sm text-red-700 text-left"
            onClick={() => onChange(plans.filter((v) => v.id !== p.id))}
          >
            Remove plan
          </button>
        </div>
      ))}
      <button
        type="button"
        className="rounded-lg border px-3 py-2 text-sm"
        onClick={() =>
          onChange([
            ...plans,
            {
              id: crypto.randomUUID(),
              name: "",
              doseLabel: "",
              path: "starter",
              productId: "",
            },
          ])
        }
      >
        Add dosage plan
      </button>
    </section>
  );
}
